import { useMemo } from 'react';
import { useAuthState } from '@vis-auth/react';
import { AccountType } from 'eve-account-client';

interface Configuration {
  basePath: string;
  accessToken?: string;
}

export class ResourceService {
  private config: Configuration;

  constructor(config: Configuration) {
    this.config = config;
  }

  async fetchResources(path: string): Promise<Response> {
    const options = this.getRequestOptions();
    try {
      return fetch(this.config.basePath + path, options);
    } catch (error) {
      throw new Error('Could not fetch resources');
    }
  }

  getRequestOptions(): object {
    if (this.config.accessToken) {
      return {
        headers: {
          Authorization: `Bearer ${this.config.accessToken}`,
        },
      };
    }
    return {};
  }

  async getMessages(): Promise<Response> {
    return this.fetchResources(`/messages`);
  }

  async getCategories(accountType: AccountType): Promise<Response> {
    return this.fetchResources(`/categories?account_type=${accountType}`);
  }

  async getSubcategories(id: number, accountType: AccountType): Promise<Response> {
    return this.fetchResources(`/categories/${id}/subcategories?account_type=${accountType}`);
  }

  async getDocument(url: string): Promise<Response> {
    return this.fetchResources(url);
  }
}

const useConfiguration = (): Configuration => {
  const { accessToken } = useAuthState();
  const VIS_RESOURCE_URL = '/api/resource';

  const configuration: Configuration = useMemo(() => {
    return { basePath: VIS_RESOURCE_URL, accessToken: accessToken as string };
  }, [VIS_RESOURCE_URL, accessToken]);

  return configuration;
};

const useResourceService = (): ResourceService => {
  const configuration = useConfiguration();
  const service = useMemo(() => new ResourceService(configuration), [configuration]);

  return service;
};

export default useResourceService;
